import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'
import { useConfirm } from 'legacy/lut/components/shared/Confirm/useConfirm'
import { getJbpBridgesCrud } from 'legacy/lut/components/widgets/public/partner360/actionsPlan/commercialBridges/components/businessBridges/helpers/getJbpBridgesCrud'
import { getBusinessBridgeCrud } from 'legacy/lut/components/widgets/public/partner360/actionsPlan/commercialBridges/cruds/getBusinessBridgeCrud'
import { KeyInitiativeEditor } from 'legacy/lut/components/widgets/public/partner360/actionsPlan/keyInitiatives/lpKeyInitiatives/components/keyInitiativeEditor/KeyInitiativeEditor'
import { IFormKIEditor } from 'legacy/lut/components/widgets/public/partner360/actionsPlan/keyInitiatives/models'
import { P360Card } from 'legacy/lut/components/widgets/public/partner360/p360Card'
import { selectCurrentCompleteness } from 'legacy/lut/features/partner360/completeness'
import { selectJBPSettings } from 'legacy/lut/features/partner360/jbpSettings'
import { useSignature } from 'legacy/lut/hooks/useSignature'
import { useUserRoles } from 'legacy/lut/hooks/useUserRoles'
import React, { useCallback, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import sanitize from 'sanitize-html'

import { InfoBlock, InfoBlockType } from 'cng-common/components/infoBlock'
import { LoaderOverlay } from 'cng-common/components/loaderOverlay'
import { RenderWhen } from 'cng-common/components/renderWhen'
import { Translation, useSafetyTranslation } from 'cng-common/features/translations'
import { useAppDispatch } from 'cng-common/hooks/useAppDispatch'
import { Button } from 'core-components/library/button'
import { Flex } from 'core-components/library/flex'
import { selectAppOwnerId } from 'core-components/sitebuilder/store/selectors/selectAppOwnerId'
import { Static } from 'core-components/types'
import { selectPublishingMode } from 'core/store/selectors/selectPublishingMode'
import { Crud } from 'core/types'
import { ConfirmationDialog } from 'partner360/components/confirmationDialog/ConfirmationDialog'
import { TOUR_TARGETS } from 'partner360/configs/widgetTour/planningActivitiesTourTargets'
import { TKeyInitiative } from 'partner360/entities/keyInitiatives'
import { useRefetchPlanningCockpitData } from 'partner360/features/financialCockpit/model/hooks/useRefetchPlanningCockpitData'
import { INITIATIVES_EDITOR_TYPE_LEGACY } from 'partner360/features/keyInitiativeEditor/model/editorConfig'
import { USE_SELECTIVE_COPY_FROM_PREVIOUS_YEAR } from 'partner360/features/keyInitiatives/configs/config'
import { TRANSLATION_KEYS } from 'partner360/features/keyInitiatives/configs/translations'
import { CopyKeyInitiativesFromPreviousYearDialog } from 'partner360/features/keyInitiatives/copyFromPreviousYear'
import {
  deleteDialogIdentifier,
  KeyInitiativeDeleteDialog,
} from 'partner360/features/keyInitiatives/ui/keyInitiativesTable/components/keyInitiativeDeleteDialog/KeyInitiativeDeleteDialog'
import { ViewSettings } from 'partner360/features/keyInitiatives/ui/viewSettings/ViewSettings'
import { recalculateCompleteness } from 'partner360/helpers/completeness/recalculateCompleteness'
import { useHoldPlanSectionOpen } from 'partner360/lib/planSectionBusy'
import { ActualsValueTypes } from 'partner360/models/actualValueTypes'
import { applyKeyInitiativeChanges } from 'partner360/services/keyInitiatives/applyKeyInitiativeChanges'

import { DisclaimerBanner } from '../common/disclaimerBanner/DisclaimerBanner'
import { useCopyFromPrevYear } from '../hooks/useCopyFromPrevYear'
import { useFilteredKeyInitiatives } from '../hooks/useFilteredKeyInitiatives'
import { useHandleShowAddNewDialog } from '../hooks/useHandleShowAddNewDialog'
import { useKeyInitiativesControlsForm } from '../hooks/useKeyInitiativesControlsForm'
import { useNewKeyInitiative } from '../hooks/useNewKeyInitiative'

import { GroupedPaginatedTable } from './components/groupedPaginatedTable/GroupedPaginatedTable'
import { getColumns } from './helpers/getColumns'
import { getFinGoalTypeTranslationKey } from './helpers/getFinGoal'
import { getGroupedInitiativeRows } from './helpers/getGroupedInitiativeRows'
import { TGroupedInitiativeRow } from './models'

import styles from './KeyInitiativesTable.module.scss'

interface IInitiativesTableProps {
  data: TKeyInitiative[]
  isCurrentYearDataLoaded: boolean
  partnerId: string
  refetchKI: () => void
  isGlobal: boolean
  isRollupMode: boolean
  fYear: number
  programType: ProgramTypes

  activeCurrency: string

  actualsType: ActualsValueTypes
  actualsTypes: ActualsValueTypes[]
  onChangeActualsType: (value: ActualsValueTypes) => void
  withControls?: boolean
}

export const KeyInitiativesTable = ({
  data,
  isCurrentYearDataLoaded,
  partnerId,
  refetchKI,
  isGlobal,
  isRollupMode,
  activeCurrency,
  actualsType,
  fYear,
  programType,
  onChangeActualsType,
  actualsTypes,
  withControls,
}: IInitiativesTableProps) => {
  const { groupBy } = useKeyInitiativesControlsForm()
  const isEditMode = useSelector(selectPublishingMode)
  const initiativesPlanMessage =
    useSelector(selectCurrentCompleteness)?.initiativesPlanMessage?.trim() || ''
  const { signatureId } = useSignature()

  const signed = signatureId ? 1 : -1

  const { keyObjectives, kiBusinessPriorities } = useSelector(selectJBPSettings)

  const ownerId = useSelector(selectAppOwnerId)
  const dispatch = useAppDispatch()
  const refetchPlanningCockpitData = useRefetchPlanningCockpitData()
  const t = useSafetyTranslation()
  const { isPartnerUser } = useUserRoles()

  const { filteredKeyInitiatives } = useFilteredKeyInitiatives({ data })

  const canEditPartner = useMemo(() => {
    return !isPartnerUser
  }, [isPartnerUser])

  const [isLoading, setIsLoading] = useState(false)

  useHoldPlanSectionOpen(isLoading)
  const [recentlyCopiedSourceIds, setRecentlyCopiedSourceIds] = useState<Set<number>>(
    () => new Set()
  )

  const handleInitiativesCopied = useCallback((sourceEntryIds: number[]) => {
    setRecentlyCopiedSourceIds((ids) => new Set([...ids, ...sourceEntryIds]))
  }, [])

  const highlightedEntryIds = useMemo(
    () =>
      new Set(
        data
          .filter(
            (initiative) =>
              initiative.FYear === fYear &&
              initiative.PartnerId === partnerId &&
              initiative.PreviousEntryId != null &&
              recentlyCopiedSourceIds.has(initiative.PreviousEntryId)
          )
          .map((initiative) => initiative.EntryId)
      ),
    [data, fYear, partnerId, recentlyCopiedSourceIds]
  )

  const {
    showCopyFromPrevYearDialog,
    openCopyFromPrevYearDialog,
    closeCopyFromPrevYearDialog,
    handleCopyFromPrevYear,
  } = useCopyFromPrevYear({
    onChangeLoading: setIsLoading,
    partnerId,
    fetch: () => refetchKI(),
  })

  const { showAddNewDialog, handleShowNewDialog, handleCloseNewDialog } =
    useHandleShowAddNewDialog()

  const { sourceItem, resetSourceItem, setSourceItem } = useNewKeyInitiative<IFormKIEditor>({
    handleShowNewDialog,
  })

  const { confirm } = useConfirm()

  const columns = useMemo(
    () => getColumns({ actualsType, activeCurrency, t, isRollupMode }),
    [isRollupMode, activeCurrency, actualsType, t]
  )

  const rows = useMemo(
    () =>
      getGroupedInitiativeRows(
        filteredKeyInitiatives,
        groupBy,
        keyObjectives,
        kiBusinessPriorities,
        actualsType,
        activeCurrency
      ).map((el) => ({
        ...el,
        Name: el.Name ? t(el.Name) : '',
        groupKey: el.groupKey ? t(el.groupKey) : '',
        finGoalTypeLabel: el.isGroupRow
          ? ''
          : el.finGoalType
            ? t(getFinGoalTypeTranslationKey(el.finGoalType))
            : '-',
      })),
    [
      filteredKeyInitiatives,
      groupBy,
      keyObjectives,
      kiBusinessPriorities,
      actualsType,
      activeCurrency,
      t,
    ]
  )

  const crud = useMemo(() => {
    return new Crud({
      fetcher: new Static(rows),
      keyField: 'EntryId',
      defaultPaging: {
        page: 0,
        pageSize: 100,
      },
    })
  }, [rows])

  const closeKIDialog = useCallback(() => {
    handleCloseNewDialog()
    resetSourceItem()
  }, [handleCloseNewDialog, resetSourceItem])

  const onUpdateIconClick = useCallback(
    (item: TGroupedInitiativeRow) => {
      setSourceItem(item as IFormKIEditor)
      handleShowNewDialog()
    },
    [handleShowNewDialog, setSourceItem]
  )

  const remove = useCallback(
    async (item: TGroupedInitiativeRow) => {
      if (await confirm(deleteDialogIdentifier)) {
        setIsLoading(true)
        try {
          const newData = await applyKeyInitiativeChanges<TKeyInitiative>({
            ownerId,
            partnerId,
            programType: item.ProgramType?.trim() ? programType : null,
            fYear,
            action: 'Delete',
            data: item,
          })

          if (item.AddToBridges === 1) {
            getBusinessBridgeCrud().removeQueries(true)
            getJbpBridgesCrud().removeQueries(true)
          }

          // Recalculate before either reload: cockpit also supplies the plan warning.
          await recalculateCompleteness({ pid: partnerId, fYear, dispatch })
          if (newData) {
            await refetchKI()
            refetchPlanningCockpitData()
          }
        } finally {
          setIsLoading(false)
        }
      }
    },
    [
      confirm,
      dispatch,
      fYear,
      ownerId,
      partnerId,
      programType,
      refetchKI,
      refetchPlanningCockpitData,
    ]
  )

  return (
    <Flex gap={'md'} flexDirection={'column'}>
      <LoaderOverlay isVisible={isLoading} />

      <RenderWhen condition={withControls}>
        <ViewSettings
          programType={programType}
          isRollupMode={isRollupMode}
          data={data}
          onChangeActualsType={onChangeActualsType}
          actualsType={actualsType}
          actualsTypes={actualsTypes}
        />
      </RenderWhen>
      <DisclaimerBanner />
      <div data-tour-id={TOUR_TARGETS.initiativesTable}>
        <P360Card>
          {initiativesPlanMessage && (
            <div className={styles.planMessageBanner}>
              <InfoBlock
                type={InfoBlockType.Warning}
                text={
                  <span dangerouslySetInnerHTML={{ __html: sanitize(initiativesPlanMessage) }} />
                }
              />
            </div>
          )}
          <Flex alignItems={'center'} flexWrap={'wrap'}>
            <RenderWhen condition={!isRollupMode && isEditMode}>
              <Button
                margin={'lg'}
                onClick={async () => {
                  handleShowNewDialog()
                }}
              >
                <Translation text={TRANSLATION_KEYS.dashboard.table.createNew} />
              </Button>
              <Button
                margin={'lg'}
                onClick={openCopyFromPrevYearDialog}
                disabled={!isCurrentYearDataLoaded}
              >
                <Translation text={TRANSLATION_KEYS.copyFromPreviousYear.open} />
              </Button>
            </RenderWhen>
            <Button
              className={styles.exportBtn}
              margin={'lg'}
              onClick={async () => {
                const exportMethod = crud.export
                if (typeof exportMethod === 'function') {
                  await exportMethod(
                    `Key Initiatives_FY${String(fYear).slice(-2)}_${programType}_${partnerId}`,
                    columns
                  )
                }
              }}
            >
              <Translation text={TRANSLATION_KEYS.dashboard.table.export} />
            </Button>
          </Flex>
          <GroupedPaginatedTable
            rows={rows}
            columns={columns}
            highlightedEntryIds={highlightedEntryIds}
            pageSize={rows.length}
            rowActionsVisible={!isRollupMode && isEditMode && canEditPartner ? 'visible' : 'hidden'}
            rowActions={[
              {
                id: 'update',
                action: onUpdateIconClick,
                title: 'Update',
                icon: 'kbase kbase-edit kbase-sm',
              },
              {
                id: 'remove',
                action: remove,
                title: 'Remove',
                icon: 'kbase kbase-delete kbase-sm',
              },
            ]}
          />
        </P360Card>
      </div>
      <RenderWhen condition={showAddNewDialog}>
        <KeyInitiativeEditor
          initialState={sourceItem}
          isDialogOpened={showAddNewDialog}
          isDataInsightPresent={programType !== ProgramTypes.Retail}
          onLoadingStateChange={setIsLoading}
          onEditCancel={closeKIDialog}
          onUpdateFinished={() => {
            closeKIDialog()
            refetchKI()
          }}
          keyInitiativesFormatOverride={isGlobal ? INITIATIVES_EDITOR_TYPE_LEGACY : undefined}
        />
      </RenderWhen>
      <KeyInitiativeDeleteDialog isCommitmentSigned={signed > 0} />
      {USE_SELECTIVE_COPY_FROM_PREVIOUS_YEAR ? (
        <CopyKeyInitiativesFromPreviousYearDialog
          show={showCopyFromPrevYearDialog}
          partnerId={partnerId}
          programType={programType}
          currentFiscalYear={fYear}
          activeCurrency={activeCurrency}
          actualsType={actualsType}
          onClose={closeCopyFromPrevYearDialog}
          onDataChanged={refetchKI}
          onCopied={() => undefined}
          onInitiativesCopied={handleInitiativesCopied}
        />
      ) : (
        <ConfirmationDialog
          onConfirm={handleCopyFromPrevYear}
          onReject={closeCopyFromPrevYearDialog}
          title={TRANSLATION_KEYS.requirements.copyKIDialog.title}
          content={TRANSLATION_KEYS.requirements.copyKIDialog.text}
          confirmBtnText={TRANSLATION_KEYS.requirements.copyKIDialog.submit}
          rejectBtnText={TRANSLATION_KEYS.requirements.copyKIDialog.cancel}
          isVisible={showCopyFromPrevYearDialog}
        />
      )}
    </Flex>
  )
}
